import { FGStack, FGTypography } from '@fg/design-system';
import FeedEmptyState from 'components/FeedEmptyState';
import React, { useCallback, useMemo } from 'react';
import { IPrediction, IActiveCountry } from '../../../interfaces';
import { isEmpty, keyBy, toString } from 'lodash/fp';
import GQLoader from 'components/GQLoader/GQLoader';
import FeedHeader from 'components/eventsFeed/FeedHeader';
import DateSeparator from 'components/eventsFeed/EventsFeedDateSeparator';
import PredictionItem from './PredictionItem';
import FullHeightScroll from 'components/FullHeightScroll';

export type PredictionWithDateString = IPrediction & {
    dateString: string;
};

type Props = {
    predictions: PredictionWithDateString[];
    isLoading: boolean;
    loadFailed?: boolean;
    loadMore: () => void;
    licensedCountriesArr: IActiveCountry[];
    onPredictionClick?: (id: number) => void;
    maxDate: moment.Moment;
    minDate: moment.Moment;
    selectedCountries: number[];
    searchString: string;
    onSetSearchString: (value: string) => void;
    removeCountry: (countryId: number) => void;
    addCountry: (countryId: number) => void;
    toggleCountry: (countryId: number) => void;
    onDateRangeChange: (startDate: Date, endDate: Date) => void;
    onDateRangeReset: () => void;
    activeId?: string;
};

const Predictions = React.memo((props: Props) => {
    //todo: DRY and add CountryById type to interfaces.ts
    const countriesById: { [countryID: number]: IActiveCountry } = useMemo(
        () =>
            keyBy(
                (country: IActiveCountry) => country.id,
                props.licensedCountriesArr
            ),
        [props.licensedCountriesArr]
    );
    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            if (scrollHeight - scrollTop - clientHeight < 200) {
                props.loadMore();
            }
        },
        [props.loadMore]
    );
    return (
        <FGStack
            style={{ height: '100%', width: '100%' }}
            direction="column"
            gap={4}>
            <FeedHeader
                isShowCountryFilter
                isShowDateFilter
                maxDate={props.maxDate}
                minDate={props.minDate}
                selectedCountries={props.selectedCountries}
                searchString={props.searchString}
                onSetSearchString={props.onSetSearchString}
                removeCountry={props.removeCountry}
                addCountry={props.addCountry}
                toggleCountry={props.toggleCountry}
                onDateRangeChange={props.onDateRangeChange}
                onDateRangeReset={props.onDateRangeReset}
            />
            <PredictionAccuracy predictions={props.predictions}/>
            <FeedEmptyState
                isLoading={props.isLoading}
                loadFailed={props.loadFailed}
                isEmpty={isEmpty(props.predictions)}
                failedMessage="Couldn't load predictions"
            />
            <FullHeightScroll onScroll={handleScroll}>
                {props.predictions.map((prediction, index) => {
                    const previousPrediction = props.predictions[index - 1];
                    const shouldDrawSeparator =
                        previousPrediction &&
                        index > 0 &&
                        prediction.dateString !== previousPrediction.dateString;
                    return (
                        <React.Fragment key={prediction.id}>
                            {shouldDrawSeparator && (
                                <DateSeparator
                                    caption={prediction.dateString}
                                />
                            )}
                            <PredictionItem
                                prediction={prediction}
                                countriesById={countriesById}
                                onClick={props.onPredictionClick}
                                isActive={props.activeId === toString(prediction.id)}
                            />
                        </React.Fragment>
                    );
                })}
            </FullHeightScroll>
            {props.isLoading && !isEmpty(props.predictions) && <GQLoader />}
        </FGStack>
    );
});

const PredictionAccuracy = React.memo(
    ({ predictions }: { predictions: IPrediction[] }) => {
        const percent = useMemo(() => {
            const scored = predictions.filter(
                p => p.outcome === 'correct' || p.outcome === 'partial'
            ).length;
            const total = predictions.filter(p => p.outcome !== 'pending')
                .length;
            return (scored / total) * 100;
        }, [predictions]);

        return (
            <FGStack
                direction="column"
                gap={4}
                style={{ marginBlock: '8px 20px' }}>
                <FGTypography tag="span" variant="label1">
                    Prediction Accuracy
                </FGTypography>
                <FGTypography tag="span" variant="headline5" color="strong">
                    {isNaN(percent) ? '0' : percent.toFixed(1)}%
                </FGTypography>
            </FGStack>
        );
    }
);

export default Predictions;
